import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  HOME_ADDRESS_ROUTE,
  HOME_ROUTE,
  LOGIN_ROUTE,
  OAUTH_CALLBACK_ROUTE,
  PRIVACY_POLICY_ROUTE,
  REGISTER_ROUTE,
  REGISTER_SUCCESS,
  REGISTER_VERIFICATION_PROCESS_ROUTE,
  REGISTRATION_VERIFICATION_ROUTE,
  RESET_PASSWORD_ROUTE,
  TERMS_AND_CONDITONS_ROUTE,
  SUPPORT_ROUTE,
  BILL_ARCHIVE,
  BILLS,
  HOUSE_MANAGER,
  USER_PROFILE,
  NEWS,
  PROJECTS_AND_REPAIR_WORKS,
  HOUSES_INFO_ROUTE,
} from "./lib/constants/routes";
import PublicRoutes from "./components/routes/PublicRoutes";
import ProtectedRoutes from "./components/routes/ProtectedRoutes";
import Footer from "./components/footer/Footer";
import { lazy, Suspense, useEffect } from "react";
import PageLoading from "./components/customComponents/PageLoading";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { useUserContext } from "./contexts/UserContextProvider";
import HouseManager from "./pages/HouseManager";
import UserProfile from "./pages/UserProfile";
import News from "./pages/News";
import ProjectsAndRepairWorks from "./pages/ProjectsAndRepairWorks";
import HousesInfoPage from "./pages/HousesInfoPage";

const NotFoundPage = lazy(() => import("./components/NotFoundPage"));
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const OAuthCallback = lazy(() => import("./pages/auth/eParakstsCallBack"));
const RegistrationPage = lazy(() => import("./pages/auth/RegistrationPage"));
const RegistrationVerification = lazy(
  () => import("./pages/auth/RegistrationVerification")
);
const EmailVerificationProcess = lazy(
  () => import("./pages/auth/EmailVerificationProcess")
);
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const TermsAndConditions = lazy(
  () => import("./pages/privacy/TermsAndConditions")
);
const PrivacyPolicy = lazy(() => import("./pages/privacy/PrivacyPolicy"));
const Support = lazy(() => import("./pages/Support"));
const SupportChat = lazy(() => import("./pages/SupportChat"));
const Bills = lazy(() => import("./pages/Bills"));
const BillsArchive = lazy(() => import("./pages/BillArchive"));
const Home = lazy(() => import("./pages/Home"));
const RegisterSucces = lazy(() => import("./pages/auth/RegisterSucces"));
const AddressPage = lazy(() => import("./pages/AddressPage"));

function OAuthRedirectHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');

    if (code && location.pathname !== OAUTH_CALLBACK_ROUTE) {
      
      const queryString = location.search;
      
      navigate(`${OAUTH_CALLBACK_ROUTE}${queryString}`, { replace: true });
    }
  }, [location, navigate]);

  return null;
}

function App() {
  const { i18n } = useTranslation();
  const { user, loading } = useUserContext();

  useEffect(() => {
    if (user) {
      i18n.changeLanguage(user.notification_language);
    }
  }, [i18n, user]);

  return loading ? (
    <PageLoading />
  ) : (
    <Suspense fallback={<PageLoading />}>
      <Router>
        <OAuthRedirectHandler />
        
        <Routes>
          <Route
            path="/"
            element={<Navigate to={user ? HOME_ROUTE : LOGIN_ROUTE} />}
          />

          <Route element={<PublicRoutes />}>
            <Route path={LOGIN_ROUTE} element={<LoginPage />} />
            <Route path={OAUTH_CALLBACK_ROUTE} element={<OAuthCallback />} />
            <Route path={REGISTER_ROUTE} element={<RegistrationPage />} />
            <Route
              path={REGISTRATION_VERIFICATION_ROUTE}
              element={<RegistrationVerification />}
            />
            <Route path={RESET_PASSWORD_ROUTE} element={<ResetPassword />} />
          </Route>

          <Route element={<ProtectedRoutes />}>
            <Route path={HOME_ROUTE} element={<Home />} />
            <Route
              path={`${HOME_ADDRESS_ROUTE}/:id`}
              element={<AddressPage />}
            />
            <Route
              path={`${HOUSES_INFO_ROUTE}/:id/:address`}
              element={<HousesInfoPage />}
            />
            <Route path={REGISTER_SUCCESS} element={<RegisterSucces />} />
            <Route path={SUPPORT_ROUTE} element={<Support />} />
            <Route path={`${SUPPORT_ROUTE}/:id`} element={<SupportChat />} />
            <Route path={`${BILL_ARCHIVE}/:id`} element={<BillsArchive />} />
            <Route path={BILLS} element={<Bills />} />
            <Route path={HOUSE_MANAGER} element={<HouseManager />} />
            <Route path={USER_PROFILE} element={<UserProfile />} />
            <Route path={NEWS} element={<News />} />
            <Route
              path={PROJECTS_AND_REPAIR_WORKS}
              element={<ProjectsAndRepairWorks />}
            />
          </Route>

          <Route path={PRIVACY_POLICY_ROUTE} element={<PrivacyPolicy />} />
          <Route
            path={TERMS_AND_CONDITONS_ROUTE}
            element={<TermsAndConditions />}
          />
          <Route
            path={REGISTER_VERIFICATION_PROCESS_ROUTE}
            element={<EmailVerificationProcess />}
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>

        <div>
          <Footer />
          <ToastContainer />
        </div>
      </Router>
    </Suspense>
  );
}

export default App;